import { appLink } from '@app/config/appLink'
import { ChevronDown } from 'lucide-react'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { AnimatePresence, motion } from 'framer-motion'
import { LogIn, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { navigation } from './navigation'
import { useAuth } from '@app/Auth/hooks/useAuth'
import { isAuthenticatedAtom } from '@app/state/authAtom'
import { useAtomValue } from 'jotai'

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const { initiateLogin } = useAuth()

  return (
    <nav className="flex items-center justify-between pt-3 pb-1 lg:px-8">
      {/* logo */}
      <div className="flex p-6 lg:flex-1">
        <Link to="/" className="-m-1.5 p-1.5">
          <span className="sr-only">Resilience Explorer®</span>
          <img src="/website/rex_logo.png" className="w-auto h-14" alt="" />
        </Link>
      </div>
      <div className="flex items-center justify-center h-full">
        <NavigationMenu.Root className="items-center justify-center hidden lg:flex lg:gap-x-5 w-full">
          <NavigationMenu.List className="flex items-center gap-x-5 w-full">
            {navigation.map((item) =>
              item.children && item.children.length > 0 ? (
                <NavigationMenu.Item key={item.name} className="list-none">
                  <NavigationMenu.Trigger className="text-sm font-semibold text-white no-underline flex items-center gap-1 px-4 py-1 rounded bg-transparent border-0 transition-colors cursor-pointer data-[state=open]:bg-text-inverted/10">
                    {item.name}
                    <ChevronDown className="w-3 h-3" />
                  </NavigationMenu.Trigger>
                  <AnimatePresence>
                    <NavigationMenu.Content>
                      <motion.div
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.22, ease: 'easeOut' }}
                        className="absolute left-0 z-10 min-w-40 py-2 mt-2 bg-neutral-muted rounded-md shadow-lg"
                      >
                        {item.children.map((child, id) => (
                          <Link
                            to={child.href}
                            key={id}
                            className="text-sm text-inherit no-underline "
                          >
                            <NavigationMenu.Item
                              key={child.name}
                              className="px-4 py-2 transition-colors hover:bg-brand-blue/10 text-sm text-brand-blue font-semibold cursor-pointer"
                            >
                              {child.name}
                            </NavigationMenu.Item>
                          </Link>
                        ))}
                      </motion.div>
                    </NavigationMenu.Content>
                  </AnimatePresence>
                </NavigationMenu.Item>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-sm font-semibold text-white no-underline px-2 py-1 rounded transition-colors"
                >
                  {item.name}
                </Link>
              ),
            )}
            <NavigationMenu.Item className="flex items-center lg:flex-1 lg:justify-center">
              {isAuthenticated ? (
                <Link
                  to={appLink.homepage.path}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white no-underline transition-colors bg-transparent rounded hover:bg-brand-blue"
                >
                  <LogIn className="inline-block w-4 h-4 ml-2" />
                  Sign In
                </Link>
              ) : (
                <button
                  className="cursor-pointer border-0 flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white no-underline transition-colors bg-transparent rounded hover:bg-brand-blue"
                  onClick={() => {
                    initiateLogin()
                  }}
                >
                  <LogIn className="inline-block w-4 h-4 ml-2" />
                  Sign In
                </button>
              )}
            </NavigationMenu.Item>
          </NavigationMenu.List>
        </NavigationMenu.Root>
      </div>

      {/* Mobile menu button */}
      <div
        role="button"
        onClick={() => setMenuOpen(true)}
        className="
    lg:hidden
    mr-5
    flex items-center justify-center
    rounded-md
    p-2.5
    cursor-pointer

    border border-text-inverted
    text-text-inverted
    transition-colors
    hover:bg-text-inverted/10
  "
      >
        <span className="sr-only">Open menu</span>
        <Menu className="size-6" />
      </div>

      {menuOpen && (
        <NavigationMenu.Root className="fixed inset-0 z-50 p-6 overflow-y-auto bg-[#0b2948] lg:hidden text-text-inverted">
          <NavigationMenu.List className="flex flex-col mb-6">
            <NavigationMenu.Item className="flex items-center justify-between mb-6 list-none">
              <Link to="/" className="-m-1.5 p-1.5">
                <img src="/website/rex_logo.png" className="w-auto h-14" alt="" />
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="appearance-none rounded-md p-2.5 text-text-inverted bg-transparent border-0 shadow-none hover:bg-text-inverted/10 transition-colors cursor-pointer"
              >
                <span className="sr-only">Close menu</span>
                <X className="size-6" />
              </button>
            </NavigationMenu.Item>
            {navigation.map((item) =>
              item.children && item.children.length > 0 ? (
                <NavigationMenu.Item key={item.name} className="list-none">
                  <NavigationMenu.Trigger className="block w-full text-left px-3 py-2 text-base font-semibold no-underline transition-colors rounded-lg text-text-inverted hover:bg-text-inverted/10 flex items-center gap-1 bg-transparent border-0">
                    {item.name}
                    <svg className="w-3 h-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" />
                    </svg>
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content className="pl-4">
                    {item.children.map((child, id) => (
                      <Link
                        to={child.href}
                        key={id}
                        className="block px-3 py-2 text-sm font-medium no-underline transition-colors rounded-lg text-white hover:bg-text-inverted/20"
                      >
                        <NavigationMenu.Item key={child.name}>{child.name}</NavigationMenu.Item>
                      </Link>
                    ))}
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              ) : (
                <NavigationMenu.Item key={item.name} className="list-none">
                  <Link
                    to={item.href}
                    className="block px-3 py-2 text-base font-semibold no-underline transition-colors rounded-lg text-text-inverted hover:bg-text-inverted/10"
                  >
                    {item.name}
                  </Link>
                </NavigationMenu.Item>
              ),
            )}
            <NavigationMenu.Item className="list-none">
              {isAuthenticated ? (
                <Link
                  to={appLink.homepage.path}
                  className="flex items-center gap-1 px-4 py-2 text-base font-semibold text-white no-underline transition-colors bg-transparent rounded hover:bg-brand-blue w-full"
                >
                  <LogIn className="inline-block w-4 h-4 ml-2" />
                  Sign In
                </Link>
              ) : (
                <button
                  className="cursor-pointer border-0 px-3 py-2 bg-transparent flex gap-1 items-center text-white rounded no-underline text-base hover:bg-brand-blue transition-colors font-semibold w-full"
                  onClick={() => {
                    initiateLogin()
                  }}
                >
                  <LogIn className="inline-block w-4 h-4 ml-2" />
                  Sign In
                </button>
              )}
            </NavigationMenu.Item>
          </NavigationMenu.List>
        </NavigationMenu.Root>
      )}
    </nav>
  )
}

export default Navbar
